import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface AppSetting {
  id: string;
  key: string;
  value: any;
  type: 'boolean' | 'string' | 'number' | 'json' | 'date';
  category: string;
  name: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface SettingControlProps {
  setting: AppSetting;
  value: any;
  onValueChange: (value: any) => void;
  disabled?: boolean;
}

export function SettingControl({ setting, value, onValueChange, disabled = false }: SettingControlProps) {
  const renderControl = () => {
    switch (setting.type) {
      case 'boolean':
        console.log('🔘 Rendering boolean control for', setting.key, ':', {
          settingValue: setting.value,
          currentValue: value,
          valueType: typeof value,
          settingType: typeof setting.value
        });
        return (
          <div className="flex items-center space-x-2">
            <Switch
              checked={value === true}
              onCheckedChange={onValueChange}
              disabled={disabled}
            />
            <Label className="text-sm text-muted-foreground">
              {value ? 'Habilitado' : 'Deshabilitado'}
            </Label>
          </div>
        );

      case 'number':
        return (
          <Input
            type="number"
            value={value || ''}
            onChange={(e) => onValueChange(Number(e.target.value) || 0)}
            disabled={disabled}
          />
        );

      case 'date':
        const selectedDate = value ? new Date(value) : undefined;
        return (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !selectedDate && "text-muted-foreground"
                )}
                disabled={disabled}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {selectedDate ? (
                  format(selectedDate, "PPP", { locale: es })
                ) : (
                  <span>Seleccionar fecha</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => onValueChange(date ? format(date, 'yyyy-MM-dd') : '')}
                initialFocus
                className="p-3 pointer-events-auto"
              />
            </PopoverContent>
          </Popover>
        );

      case 'string':
        // Use textarea for longer strings (like messages)
        if (setting.key.includes('message') || (value && value.length > 50)) {
          return (
            <Textarea
              value={value || ''}
              onChange={(e) => onValueChange(e.target.value)}
              disabled={disabled}
              rows={3}
            />
          );
        }
        return (
          <Input
            type="text"
            value={value || ''}
            onChange={(e) => onValueChange(e.target.value)}
            disabled={disabled}
          />
        );

      case 'json':
        return (
          <Textarea
            value={JSON.stringify(value, null, 2)}
            onChange={(e) => {
              try {
                onValueChange(JSON.parse(e.target.value));
              } catch {
                // Invalid JSON, keep the text as is for now
              }
            }}
            disabled={disabled}
            rows={4}
            className="font-mono text-sm"
          />
        );

      default:
        return <span className="text-muted-foreground">Tipo no soportado</span>;
    }
  };

  return (
    <div className="space-y-4 p-4 border rounded-lg">
      <div className="space-y-2">
        <Label className="text-base font-medium">{setting.name}</Label>
        {setting.description && (
          <p className="text-sm text-muted-foreground">{setting.description}</p>
        )}
      </div>

      <div className="space-y-3">
        {renderControl()}
      </div>
    </div>
  );
}
